import {
  STORE_CONFIRM_SIZES_IN_STOCK_MSG_COLOR,
  STORE_CONFIRM_SIZES_SOLD_OUT_NO_RESTOCK_TOOLTIP,
  STORE_CONFIRM_SIZES_SOLD_OUT_RESTOCK_TOOLTIP_PREFIX,
} from '../../constants/storeCheckout.constants';
import {
  STOCK_STATUS_ENUM,
  type ISizeSelectedState,
  type IStockStatusProps,
  type IStocksData,
  type StockStatusEnum,
} from './storeConfirmSizes.types';
import type { IStockData } from '@/common/interfaces/stocks.interface';
import { formatDateToMonthDay } from '@/utilities/stockUtils';

export { getStockColorCodeCandidates } from '@/utilities/stockUtils';

const DEFAULT_OUT_OF_STOCK_STYLES = {
  backgroundColor: '#FFF4F4',
  borderColor: '#FFD5D5',
  fontColor: '#000000',
  iconColor: '#E86868',
};

const DEFAULT_LOW_STOCK_STYLES = {
  backgroundColor: '#FFF8F2',
  borderColor: '#FFE3CC',
  fontColor: '#000000',
  iconColor: '#F49B50',
};

const OPS_APPROVAL_MSG_NO_RESTOCK_DATE_STORE =
  'This store will need to be approved by Ops. Please add a note or upload confirmation for faster approval.';

const OPS_APPROVAL_MSG_SINGLE_STORE =
  'If you need that size, this store will need to be approved by Ops.';

const OPS_APPROVAL_MSG_MULTIPLE_STORE =
  'If you need those sizes, this store will need to be approved by Ops.';

const AUTO_CHECK_STOCK_MSG_STORE = 'We can’t auto check stock or sizes for this product.';

const formatSizesList = (sizes: string[]): string => {
  if (sizes.length === 0) return '';
  if (sizes.length === 1) return sizes[0];
  if (sizes.length === 2) return `${sizes[0]} & ${sizes[1]}`;
  return `${sizes.slice(0, -1).join(', ')} & ${sizes[sizes.length - 1]}`;
};

const createOutOfStockCheckoutMessage = (stocks: IStockData[]): string => {
  const outSizes = stocks
    .filter((s) => s.status === STOCK_STATUS_ENUM.OUT_OF_STOCK)
    .map((s) => s.size);
  if (!outSizes.length) return '';
  const sizesText = formatSizesList(outSizes);
  return `${sizesText} ${outSizes.length === 1 ? 'is' : 'are'} sold out.`;
};

const createLowStockCheckoutMessage = (stocks: IStockData[]): string => {
  const lowSizes = stocks.filter(
    (s) => s.status === STOCK_STATUS_ENUM.LOW_IN_STOCK || s.status === STOCK_STATUS_ENUM.IN_STOCK
  );
  if (!lowSizes.length) return '';
  const parts = lowSizes.map((s) => `${s.size} (only ${s.qty} left)`);
  return `Low stock: ${formatSizesList(parts)}.`;
};

export const createNoStockInfoMessages = (
  _orderItemId: string,
  isStore = true
): IStockStatusProps => ({
  checkoutMessage: isStore
    ? AUTO_CHECK_STOCK_MSG_STORE
    : "We can't auto check stock for this product.",
  status: STOCK_STATUS_ENUM.OUT_OF_STOCK,
  message: isStore ? AUTO_CHECK_STOCK_MSG_STORE : "We can't auto check stock for this product.",
  opsApprovalMessage: OPS_APPROVAL_MSG_NO_RESTOCK_DATE_STORE,
  showOpsApprovalMessage: true,
  ...DEFAULT_OUT_OF_STOCK_STYLES,
  fontColor: '#D35555',
});

export const buildUnavailableStocksDataForProduct = (
  sizeLabels: string[],
  colorCode: string,
  orderItemId: string
): IStocksData => {
  const stockData = sizeLabels.map((size) => ({
    size,
    qty: 0,
    status: STOCK_STATUS_ENUM.STOCK_DATA_UNAVAILABLE,
    core_size: true,
    restock_date: '',
  }));

  return {
    color_code: colorCode,
    stock_data: stockData,
    stockDataOnSizes: stockData,
    selectedSizesStockInfo: stockData,
    stockProps: createNoStockInfoMessages(orderItemId, true),
    isFpExclusiveAndLowOrOutOfStock: false,
  };
};

export const createStockStatusMessage = (
  stocksData: IStockData[],
  isStore = true
): IStockStatusProps | undefined => {
  if (!stocksData?.length) return undefined;

  const outOfStockSizes = stocksData.filter((s) => s.status === STOCK_STATUS_ENUM.OUT_OF_STOCK);
  const lowStockSizes = stocksData.filter(
    (s) => s.status === STOCK_STATUS_ENUM.LOW_IN_STOCK || s.status === STOCK_STATUS_ENUM.IN_STOCK
  );
  const unavailableSizes = stocksData.filter(
    (s) => s.status === STOCK_STATUS_ENUM.STOCK_DATA_UNAVAILABLE
  );

  if (unavailableSizes.length > 0) {
    return createNoStockInfoMessages('', isStore);
  }

  let status: StockStatusEnum = STOCK_STATUS_ENUM.IN_STOCK;
  let styles = DEFAULT_LOW_STOCK_STYLES;
  let opsApprovalMessage = OPS_APPROVAL_MSG_SINGLE_STORE;
  let showOpsApprovalMessage = true;

  if (outOfStockSizes.length > 0 && lowStockSizes.length > 0) {
    status = STOCK_STATUS_ENUM.LOW_AND_OUT_OF_STOCK;
    styles = DEFAULT_OUT_OF_STOCK_STYLES;
    opsApprovalMessage =
      outOfStockSizes.length > 1 ? OPS_APPROVAL_MSG_MULTIPLE_STORE : OPS_APPROVAL_MSG_SINGLE_STORE;
  } else if (outOfStockSizes.length > 0) {
    status = STOCK_STATUS_ENUM.OUT_OF_STOCK;
    styles = DEFAULT_OUT_OF_STOCK_STYLES;
    opsApprovalMessage =
      outOfStockSizes.length > 1 ? OPS_APPROVAL_MSG_MULTIPLE_STORE : OPS_APPROVAL_MSG_SINGLE_STORE;
  } else if (lowStockSizes.length > 0) {
    status = STOCK_STATUS_ENUM.LOW_IN_STOCK;
    styles = DEFAULT_LOW_STOCK_STYLES;
    showOpsApprovalMessage = false;
  } else {
    return undefined;
  }

  const outMsg = createOutOfStockCheckoutMessage(stocksData);
  const lowMsg = createLowStockCheckoutMessage(stocksData);
  const checkoutMessage = [outMsg, lowMsg].filter(Boolean).join(' ');

  return {
    status,
    message: checkoutMessage,
    checkoutMessage,
    opsApprovalMessage,
    showOpsApprovalMessage,
    ...styles,
  };
};

const getSizesLabel = (sizes: string): string[] => {
  if (!sizes) return [];
  return sizes
    .split(';')
    .filter(Boolean)
    .map((pair) => pair.split(':')[1])
    .filter(Boolean);
};

export const buildStocksDataForProduct = (
  stocksResponse: { stock_data: IStockData[]; color_code?: string },
  selectedSizeLabels: string[],
  apparelSizes: string,
  isFpExclusiveAndLowOrOutOfStock = false
): IStocksData => {
  const selectedSizesStockInfo: IStockData[] = [];

  const filteredStockOnSizes = selectedSizeLabels
    .map((sizeLabel) => {
      const stockRes = stocksResponse.stock_data.find((item) => item.size === sizeLabel);

      if (!stockRes) {
        const stockObj: IStockData = {
          size: sizeLabel,
          qty: 0,
          status: STOCK_STATUS_ENUM.STOCK_DATA_UNAVAILABLE,
          core_size: true,
          restock_date: '',
        };
        selectedSizesStockInfo.push(stockObj);
        return stockObj;
      }

      selectedSizesStockInfo.push(stockRes);

      if (
        stockRes.status === STOCK_STATUS_ENUM.OUT_OF_STOCK ||
        stockRes.status === STOCK_STATUS_ENUM.LOW_AND_OUT_OF_STOCK ||
        stockRes.status === STOCK_STATUS_ENUM.LOW_IN_STOCK
      ) {
        return stockRes;
      }

      return null;
    })
    .filter((item): item is IStockData => item !== null);

  const apparelSizeLabels = getSizesLabel(apparelSizes);
  const selectedNonApparelSizes = selectedSizesStockInfo
    .filter((item) => !apparelSizeLabels.includes(item.size))
    .map((item) => item.size);

  let stockProps: IStockStatusProps | null = null;
  if (selectedNonApparelSizes.length > 0) {
    stockProps = createNoStockInfoMessages('', true);
  } else {
    stockProps =
      createStockStatusMessage(
        filteredStockOnSizes.length ? filteredStockOnSizes : stocksResponse.stock_data,
        true
      ) ?? null;
  }

  return {
    ...stocksResponse,
    stockDataOnSizes: filteredStockOnSizes.length
      ? filteredStockOnSizes
      : stocksResponse.stock_data,
    stockProps,
    selectedSizesStockInfo,
    isFpExclusiveAndLowOrOutOfStock,
  };
};

const getSizeStockEntry = (
  sizeLabel: string,
  stockData?: IStocksData | null
): IStockData | undefined => stockData?.stock_data?.find((stock) => stock.size === sizeLabel);

export const isSizeSoldOut = (sizeLabel: string, stockData?: IStocksData | null): boolean => {
  const sizeStockData = getSizeStockEntry(sizeLabel, stockData);

  return (
    sizeStockData?.status === STOCK_STATUS_ENUM.OUT_OF_STOCK ||
    sizeStockData?.status === STOCK_STATUS_ENUM.LOW_AND_OUT_OF_STOCK
  );
};

export const isSizeStockInfoUnavailable = (
  sizeLabel: string,
  stockData?: IStocksData | null
): boolean => {
  if (!stockData?.stock_data?.length) {
    return false;
  }

  const sizeStockData = getSizeStockEntry(sizeLabel, stockData);

  return !sizeStockData || sizeStockData.status === STOCK_STATUS_ENUM.STOCK_DATA_UNAVAILABLE;
};

export const isSizeRestrictedByStock = (
  sizeLabel: string,
  stockData?: IStocksData | null
): boolean =>
  isSizeSoldOut(sizeLabel, stockData) || isSizeStockInfoUnavailable(sizeLabel, stockData);

export const getSoldOutSizeTooltipMessage = (
  sizeLabel: string,
  stockData?: IStocksData | null
): string => {
  if (isSizeStockInfoUnavailable(sizeLabel, stockData)) {
    return STORE_CONFIRM_SIZES_SOLD_OUT_NO_RESTOCK_TOOLTIP;
  }

  if (!isSizeSoldOut(sizeLabel, stockData)) {
    return '';
  }

  const restockDate = getSizeStockEntry(sizeLabel, stockData)?.restock_date?.trim();

  if (restockDate) {
    return `${STORE_CONFIRM_SIZES_SOLD_OUT_RESTOCK_TOOLTIP_PREFIX} ${formatDateToMonthDay(
      restockDate
    )}`;
  }

  return STORE_CONFIRM_SIZES_SOLD_OUT_NO_RESTOCK_TOOLTIP;
};

export const checkSizeOutOfStockOrLowStock = (
  size: string,
  stockData: IStocksData | null | undefined
): IStockData | undefined => {
  if (!stockData?.stock_data) return undefined;

  return stockData.stock_data.find(
    (stock) =>
      stock.size === size &&
      (stock.status === STOCK_STATUS_ENUM.OUT_OF_STOCK ||
        stock.status === STOCK_STATUS_ENUM.LOW_IN_STOCK ||
        stock.status === STOCK_STATUS_ENUM.LOW_AND_OUT_OF_STOCK ||
        stock.status === STOCK_STATUS_ENUM.IN_STOCK)
  );
};

export const getSizeInputWarningMsg = (
  size: string,
  stockData: IStocksData | null | undefined
): string => {
  if (isSizeStockInfoUnavailable(size, stockData)) {
    return 'Sold Out';
  }

  const sizeStockData = checkSizeOutOfStockOrLowStock(size, stockData);

  if (!sizeStockData) return '';

  if (
    sizeStockData.status === STOCK_STATUS_ENUM.OUT_OF_STOCK ||
    sizeStockData.status === STOCK_STATUS_ENUM.LOW_AND_OUT_OF_STOCK
  ) {
    return 'Sold Out';
  }

  if (sizeStockData.status === STOCK_STATUS_ENUM.LOW_IN_STOCK) {
    return `Only ${sizeStockData.qty} left`;
  }

  return '';
};

export const getSizeWarningMsgColor = (
  size: string,
  stockData: IStocksData | null | undefined
): string => {
  if (isSizeStockInfoUnavailable(size, stockData)) {
    return '#E86868';
  }

  if (!stockData?.stock_data) return '';

  const sizeStockData = stockData.stock_data.find((stock) => stock.size === size);

  if (!sizeStockData) return '';

  if (
    sizeStockData.status === STOCK_STATUS_ENUM.OUT_OF_STOCK ||
    sizeStockData.status === STOCK_STATUS_ENUM.LOW_AND_OUT_OF_STOCK
  ) {
    return '#E86868';
  }

  if (sizeStockData.status === STOCK_STATUS_ENUM.LOW_IN_STOCK) {
    return '#F49B50';
  }

  if (sizeStockData.status === STOCK_STATUS_ENUM.IN_STOCK) {
    return STORE_CONFIRM_SIZES_IN_STOCK_MSG_COLOR;
  }

  return '';
};

const isSizeInStock = (sizeLabel: string, stockData: IStocksData | null | undefined): boolean => {
  if (!stockData?.stock_data?.length) return true;

  const sizeStockData = stockData.stock_data.find((stock) => stock.size === sizeLabel);
  if (!sizeStockData) return false;

  return (
    sizeStockData.status !== STOCK_STATUS_ENUM.OUT_OF_STOCK &&
    sizeStockData.status !== STOCK_STATUS_ENUM.STOCK_DATA_UNAVAILABLE
  );
};

/** Sizes available for the product and in stock (when stock data has loaded). */
export const filterSizesForProductDisplay = (
  sizes: ISizeSelectedState[],
  stockData?: IStocksData | null
): ISizeSelectedState[] =>
  sizes.filter(
    (size) => size.lockedByExistingOrder || (!size.disabled && isSizeInStock(size.label, stockData))
  );

export interface ISizeStockDisplayInfo {
  warningMsg: string;
  warningColor: string;
  showWarning: boolean;
  isSoldOut: boolean;
}

export const resolveSizeStockDisplayInfo = ({
  sizeLabel,
  stockData,
}: {
  sizeLabel: string;
  stockData?: IStocksData | null;
}): ISizeStockDisplayInfo => {
  const isSoldOut =
    isSizeSoldOut(sizeLabel, stockData) || isSizeStockInfoUnavailable(sizeLabel, stockData);
  const warningMsg = getSizeInputWarningMsg(sizeLabel, stockData);
  const warningColor = getSizeWarningMsgColor(sizeLabel, stockData);

  return {
    warningMsg,
    warningColor,
    showWarning: Boolean(warningMsg),
    isSoldOut,
  };
};

export const applySoldOutRestrictionsToSizesSelected = (
  sizesSelected: Record<string, ISizeSelectedState>,
  sizesAvailable: Record<string, boolean>,
  stockData?: IStocksData | null
): {
  sizesSelected: Record<string, ISizeSelectedState>;
  sizesAvailable: Record<string, boolean>;
} => {
  const nextSelected = { ...sizesSelected };
  const nextAvailable = { ...sizesAvailable };

  Object.entries(nextSelected).forEach(([label, size]) => {
    if (!isSizeRestrictedByStock(label, stockData)) {
      return;
    }

    if (size.lockedByExistingOrder) {
      nextSelected[label] = { ...size, disabled: true };
      return;
    }

    nextSelected[label] = { ...size, selected: false, disabled: true };
    nextAvailable[label] = false;
  });

  return { sizesSelected: nextSelected, sizesAvailable: nextAvailable };
};

export const mergeSoldOutSizeDisplayState = (
  size: ISizeSelectedState,
  stockData?: IStocksData | null
): ISizeSelectedState => {
  if (size.lockedByExistingOrder) {
    return { ...size, disabled: true };
  }

  if (!isSizeRestrictedByStock(size.label, stockData)) {
    let { disabled } = size;
    if (size.selected) {
      disabled = false;
    }
    return { ...size, disabled };
  }

  return {
    ...size,
    selected: false,
    disabled: true,
  };
};
